/**
 * Manual adjustment dialog. Used from the party-ledger screen.
 *
 * Adjustments support double-sided (typical) or single-sided entries. A
 * single-sided adjustment requires explicit user confirmation via the
 * `allowSingleSided` checkbox, mirroring the HTTP layer's gate.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Adjustment, LedgerEntry, Party } from '../api';
import {
  Button,
  Checkbox,
  ErrorBanner,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { fileToBase64 } from '../lib/file';
import { inrToKinrString, inrToKinrPreview } from '../money/inr';

export interface AdjustmentDialogProps {
  preselectDrPartyId?: number | null;
  preselectCrPartyId?: number | null;
  onClose: () => void;
  onCreated: (adj: Adjustment) => void;
}

export function AdjustmentDialog({
  preselectDrPartyId,
  preselectCrPartyId,
  onClose,
  onCreated,
}: AdjustmentDialogProps) {
  const qc = useQueryClient();
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const [drPartyId, setDrPartyId] = useState(
    preselectDrPartyId ? String(preselectDrPartyId) : '',
  );
  const [crPartyId, setCrPartyId] = useState(
    preselectCrPartyId ? String(preselectCrPartyId) : '',
  );
  const [effectiveDate, setEffectiveDate] = useState(() => todayIso());
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [allowSingleSided, setAllowSingleSided] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const partiesById = useMemo(
    () => new Map((parties.data ?? []).map((p) => [p.id, p])),
    [parties.data],
  );
  const isSingleSided = drPartyId === '' || crPartyId === '';
  const drParty = drPartyId ? partiesById.get(Number(drPartyId)) : undefined;
  const crParty = crPartyId ? partiesById.get(Number(crPartyId)) : undefined;

  const createMut = useMutation({
    mutationFn: async () => {
      if (drPartyId === '' && crPartyId === '') throw new Error('At least one party is required.');
      if (drPartyId !== '' && drPartyId === crPartyId) throw new Error('Dr and Cr parties must differ.');
      if (!description.trim()) throw new Error('Description is required.');
      if (!amount.trim()) throw new Error('Amount is required.');
      const kinrAmount = inrToKinrString(amount);
      if (kinrAmount === null) throw new Error('Amount is not a valid number.');
      if (isSingleSided && !allowSingleSided) {
        throw new Error('Check "Allow single-sided" to confirm this is intentional.');
      }
      const adj = await api.post<Adjustment>('/api/adjustments', {
        effectiveDate,
        description: description.trim(),
        amount: kinrAmount,
        drPartyId: drPartyId === '' ? null : Number(drPartyId),
        crPartyId: crPartyId === '' ? null : Number(crPartyId),
        note: note.trim() || null,
        allowSingleSided: isSingleSided ? true : undefined,
      });
      // Attach files to the Dr-side entry if present, else the Cr-side entry.
      if (files.length > 0) {
        const probeParty =
          adj.drPartyId ??
          adj.crPartyId ??
          (parties.data ?? []).find((p) => p.role === 'self')?.id;
        if (probeParty) {
          const entries = await api.get<LedgerEntry[]>(
            `/api/ledger-entries?party=${probeParty}&from=${adj.effectiveDate}&to=${adj.effectiveDate}`,
          );
          const entry = entries.find((e) => e.sourceAdjustmentId === adj.id);
          if (entry) {
            for (const f of files) {
              const base64 = await fileToBase64(f);
              await api.post(`/api/ledger-entries/${entry.id}/attachments`, {
                base64,
                originalFilename: f.name,
                mimeType: f.type || 'application/octet-stream',
              });
            }
          }
        }
      }
      return adj;
    },
    onSuccess: (a) => {
      void qc.invalidateQueries();
      onCreated(a);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const partyOptions = (parties.data ?? []).map((p) => ({
    value: String(p.id),
    label: `${p.displayName} (${p.role})`,
  }));

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="flex min-h-[60vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-surface shadow-card">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Record adjustment</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>

        <form
          className="flex-1 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            createMut.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Dr party (debit)"
              value={drPartyId}
              onChange={setDrPartyId}
              options={[{ value: '', label: '— none (self auto-offset) —' }, ...partyOptions]}
              placeholder="Choose…"
            />
            <Select
              label="Cr party (credit)"
              value={crPartyId}
              onChange={setCrPartyId}
              options={[{ value: '', label: '— none (self auto-offset) —' }, ...partyOptions]}
              placeholder="Choose…"
            />
            <TextInput
              label="Effective date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <div className="flex flex-col">
              <TextInput
                label="Amount (₹ INR)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 24864"
                inputMode="decimal"
              />
              {amount.trim().length > 0 && (
                <p className={`mt-1 text-caption ${
                  inrToKinrPreview(amount) ? 'text-pos' : 'text-neg'
                }`}>
                  {inrToKinrPreview(amount) ?? 'Not a valid amount'}
                  {inrToKinrPreview(amount) && (
                    <span className="ml-1 text-ink-muted">(stored value)</span>
                  )}
                </p>
              )}
            </div>
            <div className="col-span-full">
              <TextInput
                label="Description (appears on the ledger)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. ANNU 519721"
              />
            </div>
            <div className="col-span-full">
              <TextArea
                label="Note (internal)"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          {isSingleSided && (drPartyId !== '' || crPartyId !== '') && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <Checkbox
                label="Yes, this is a single-sided adjustment (self auto-offset)"
                hint="The other side will post to the self party, net to zero. Use only when the offset is genuinely on your own books."
                checked={allowSingleSided}
                onChange={setAllowSingleSided}
              />
              <div className="mt-3 text-caption text-amber-700">
                Preview:
                {drPartyId !== '' && (
                  <div>Dr {amount || '0'} → {drParty?.displayName}</div>
                )}
                {crPartyId !== '' && (
                  <div>Cr {amount || '0'} → {crParty?.displayName}</div>
                )}
                <div>Other side → <em>self</em></div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <label className="block text-caption font-medium text-ink">
              Attach files (optional)
            </label>
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              className="mt-1 block w-full text-caption text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface-raised file:px-3 file:py-1 file:text-small file:text-ink"
              onChange={(e) => {
                if (!e.target.files) return;
                setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
              }}
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-caption text-ink-muted">
                {files.map((f, i) => (
                  <li key={i}>{f.name} ({(f.size / 1024).toFixed(0)} kB)</li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Record adjustment'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}
